import { useDeviceType } from "@dash/mq";
import { HeaderBar, Page, SectionHeader } from "@dash/page";
import { QuerySkeleton } from "@dash/skeleton";
import { PageTitleManager } from "@legacy-megarax/ui-components";
import { useNavigate, useParams } from "react-router-dom-v5-compat";

import { useClientManager } from "@megaron/react-clients";
import { TicketDetailDto } from "@megaron/support-contracts";
import { Uuid } from "@megaron/uuid";

import { SupportTicketActiveCartCard } from "./SupportTicketActiveCartCard";
import { SupportTicketContactCrmCard } from "./SupportTicketContactCrmCard";
import { SupportTicketHeaderActions } from "./SupportTicketHeaderActions";
import { SupportTicketMessagesAndNotes } from "./SupportTicketMessagesAndNotes";
import { SupportTicketMetaSection } from "./SupportTicketMetaSection";

export const SupportTicketPage = ({ listPath = "/support/tickets" }: { listPath?: string }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isMobile } = useDeviceType();
  const getTicketParams = id
    ? { id: Uuid(id) }
    : { id: Uuid("00000000-0000-0000-0000-000000000000") };
  const ticketQuery = useClientManager("support")
    .getTicket()
    .useQuery(getTicketParams, { enabled: !!id });

  return (
    <>
      <QuerySkeleton query={ticketQuery} height="53px" width="100%">
        {(ticket: TicketDetailDto) => (
          <>
            <PageTitleManager title={ticket.subject} />
            <HeaderBar
              header={ticket.subject}
              actions={<SupportTicketHeaderActions ticket={ticket} />}
              onGoBack={() => navigate(listPath)}
            />
          </>
        )}
      </QuerySkeleton>
      <Page
        css={{
          marginTop: isMobile ? "53px" : "83px",
          padding: isMobile ? "0 1rem" : "0 2rem",
          marginBottom: "2rem",
        }}
      >
        <QuerySkeleton query={ticketQuery}>
          {(ticket: TicketDetailDto) => (
            <div
              css={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? "1rem" : "2rem",
              }}
            >
              {isMobile ? (
                <>
                  <div
                    css={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      minWidth: 0,
                      gap: "1rem",
                      marginTop: "0.75rem",
                    }}
                  >
                    <SupportTicketMetaSection ticket={ticket} listPath={listPath} />
                  </div>
                  <div
                    css={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "32px",
                    }}
                  >
                    <SectionHeader isHr>Dane klienta</SectionHeader>
                    <SupportTicketContactCrmCard
                      ticketId={ticket.id}
                      subject={ticket.subject}
                      status={ticket.status}
                      priority={ticket.priority}
                      category={ticket.category}
                      reporterEmail={ticket.reporterEmail}
                      crmCustomerUuid={ticket.crmCustomerUuid}
                      tags={ticket.tags}
                    />
                    <SupportTicketActiveCartCard crmCustomerUuid={ticket.crmCustomerUuid} />
                    <SupportTicketMessagesAndNotes ticket={ticket} />
                  </div>
                </>
              ) : (
                <>
                  <div
                    css={{
                      display: "flex",
                      flexDirection: "column",
                      flex: "1 1 auto",
                      gap: "32px",
                    }}
                  >
                    <SupportTicketContactCrmCard
                      ticketId={ticket.id}
                      subject={ticket.subject}
                      status={ticket.status}
                      priority={ticket.priority}
                      category={ticket.category}
                      reporterEmail={ticket.reporterEmail}
                      crmCustomerUuid={ticket.crmCustomerUuid}
                      tags={ticket.tags}
                    />
                    <SupportTicketActiveCartCard crmCustomerUuid={ticket.crmCustomerUuid} />
                    <SupportTicketMessagesAndNotes ticket={ticket} />
                  </div>
                  <div
                    css={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      minWidth: "19rem",
                      gap: "1rem",
                    }}
                  >
                    <SupportTicketMetaSection ticket={ticket} listPath={listPath} />
                  </div>
                </>
              )}
            </div>
          )}
        </QuerySkeleton>
      </Page>
    </>
  );
};
